import { formatMinor } from '../../../shared/amount.js';
import { getContent } from '../../../shared/content.js';
import { roundingExamples } from '../../../shared/rounding.js';
import { Blocks, RichText } from '../../components/RichText.js';
import { useLocale } from '../../i18n.js';
import { AdSlot } from '../AdSlot.js';

/**
 * How it works.
 *
 * Trust page. It explains the decision rule, the minimum group size, the rounding and
 * what happens to the raw answers, in that order, because that is the order people ask
 * about them.
 *
 * The worked examples are computed by calling the real rounding function at build time,
 * so this page cannot describe behaviour the product does not have.
 */

/** Illustrative lowest-submitted amounts, shown in the most common currency. */
const EXAMPLE_AMOUNTS = [87, 137, 317, 675, 1234, 4999] as const;

export function HowItWorks() {
  const c = getContent(useLocale()).howItWorks;

  return (
    <>
      <section className="section">
        <div className="section__inner section__inner--narrow prose">
          <h1 className="section__title">{c.heading}</h1>
          <p className="section__lede">{c.lede}</p>

          <Blocks blocks={c.blocks} />

          <h2>{c.roundingHeading}</h2>
          {c.roundingIntro.map((paragraph) => (
            <p key={paragraph.slice(0, 24)}>
              <RichText text={paragraph} />
            </p>
          ))}

          <table className="table">
            <caption className="table__caption">{c.tableCaption}</caption>
            <thead>
              <tr>
                <th scope="col">{c.tableHeadLowest}</th>
                <th scope="col">{c.tableHeadStep}</th>
                <th scope="col">{c.tableHeadCap}</th>
              </tr>
            </thead>
            <tbody>
              {roundingExamples('EUR', EXAMPLE_AMOUNTS).map((example) => (
                <tr key={example.rawMinor}>
                  <td className="tabular">{formatMinor(example.rawMinor, 'EUR')}</td>
                  <td className="tabular">
                    {c.tableStepPrefix} {formatMinor(example.stepMinor, 'EUR')}
                  </td>
                  <td className="tabular">{formatMinor(example.capMinor, 'EUR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>{c.monotonic}</p>

          <Blocks blocks={c.afterTable} />
        </div>
      </section>

      <AdSlot slot="how-it-works-footer" />
    </>
  );
}
